import { useSagaAuth, Sentry } from "@einride/saga-core"

interface ILogger {
  info: (msg: string) => void
  debug: (msg: string) => void
  warning: (msg: string) => void
  error: (err: Error | unknown) => void
}

type Fields = {
  organizationName?: string
  spaceName?: string
  userId?: string
}

const message = (fields?: Fields) => (level: Sentry.SeverityLevel) => (msg: string) =>
  Sentry.captureMessage(msg, {
    level,
    extra: {
      ...fields,
    },
  })

const internalLogger = (fields?: Fields): ILogger => ({
  info: (msg: string) => message(fields)("info")(msg),
  debug: (msg: string) => message(fields)("debug")(msg),
  warning: (msg: string) => message(fields)("warning")(msg),
  error: (err: Error | unknown) => {
    Sentry.captureException(err)
  },
})

/**
 * This method returns a raw logger without any extra information that can be used to log messages to Sentry
 *
 * @function
 * @name Logger
 * @return {ILogger} Methods that will log to Sentry with different severity levels
 */
export const Logger: ILogger = internalLogger()

/**
 * This method returns a logger that can be used to log messages to Sentry
 * It will append extra information about the user for debug purposes
 *
 * @hook
 * @name useLogger
 * @return {ILogger} Methods that will log to Sentry with different severity levels
 */
export const useLogger = (): ILogger => {
  const auth = useSagaAuth()
  // Avoid logging things that break GDPR, such as display name, email etc.
  return internalLogger({
    userId: auth?.user?.uid ?? "",
    organizationName: auth?.activeOrganization?.name ?? "",
    spaceName: auth?.activeSpace?.name ?? "",
  })
}
